import React from 'react'
import { motion } from 'framer-motion'
import { useLanguage } from '@/contexts/LanguageContext'

const AboutScreen: React.FC = () => {
  const { t } = useLanguage()
  
  const values = [
    {
      number: '01',
      title: t.about.values.sustainability,
      description: t.about.values.sustainabilityDesc,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      number: '02',
      title: t.about.values.innovation,
      description: t.about.values.innovationDesc,
      color: 'from-purple-500 to-pink-500'
    },
    {
      number: '03',
      title: t.about.values.efficiency,
      description: t.about.values.efficiencyDesc,
      color: 'from-green-500 to-emerald-500'
    },
    {
      number: '04',
      title: t.about.values.quality,
      description: t.about.values.qualityDesc,
      color: 'from-orange-500 to-red-500'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Company Story - Modern Design */}
      <section className="py-4 md:py-10 bg-white relative overflow-hidden">
        {/* Abstract Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-0 w-72 h-72 bg-primary-100/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-0 w-96 h-96 bg-secondary-100/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            {/* Accent line */}
            <div className="mb-6 md:mb-8">
              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary-300 to-transparent"></div>
                <span className="text-xs md:text-sm uppercase tracking-wider font-semibold text-primary-600">
                  {t.about.storyLabel}
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary-300 to-transparent"></div>
              </div>
            </div>

            {/* Story Content - Clean Typography Focus */}
            <div className="space-y-8 md:space-y-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-primary-500 via-primary-400 to-transparent"></div>
                <div className="pl-6 md:pl-8">
                  <p className="text-base md:text-lg lg:text-xl text-secondary-700 leading-relaxed font-light">
                    {t.about.storyParagraph1}
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-secondary-500 via-secondary-400 to-transparent"></div>
                <div className="pl-6 md:pl-8">
                  <p className="text-base md:text-lg lg:text-xl text-secondary-700 leading-relaxed font-light">
                    {t.about.storyParagraph2}
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-primary-400 via-primary-300 to-transparent"></div>
                <div className="pl-6 md:pl-8">
                  <p className="text-base md:text-lg lg:text-xl text-secondary-700 leading-relaxed font-light">
                    {t.about.storyParagraph3}
                  </p>
                </div>
              </motion.div>

              {/* Highlight Box - Clean and Minimal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                className="mt-12 md:mt-16 relative"
              >
                <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-primary-100 rounded-2xl p-6 md:p-10">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div className="flex-1">
                      <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">
                        {t.about.turbineCountTitle}
                      </h3>
                      <p className="text-sm md:text-base text-secondary-600 leading-relaxed">
                        {t.about.turbineCountDescription}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="text-5xl md:text-6xl font-bold bg-gradient-to-br from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                        150+
                      </div>
                      <div className="text-xs md:text-sm text-secondary-500 uppercase tracking-wider mt-1">
                        {t.about.turbineLabel}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision - Clean Modern Design */}
      <section className="py-12 md:py-20 bg-gray-50 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #e5e7eb 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {/* Mission */}
              <motion.div 
                className="group relative bg-white rounded-2xl p-8 md:p-10 shadow-sm hover:shadow-2xl transition-all duration-500 border-l-4 border-primary-500"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
              >
                <div className="mb-6">
                  <div className="inline-block px-4 py-1 bg-primary-50 rounded-full text-xs uppercase tracking-wider font-semibold text-primary-700 mb-4">
                    {t.about.mission}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 group-hover:text-primary-600 transition-colors duration-300">
                    {t.about.missionTitle}
                  </h3>
                </div>
                <p className="text-sm md:text-base text-secondary-600 leading-relaxed">
                  {t.about.missionDescription}
                </p>
                
                {/* Decorative element */}
                <div className="absolute top-8 right-8 w-20 h-20 bg-primary-100 rounded-full opacity-0 group-hover:opacity-20 blur-2xl transition-opacity duration-500"></div>
              </motion.div>

              {/* Vision */}
              <motion.div 
                className="group relative bg-white rounded-2xl p-8 md:p-10 shadow-sm hover:shadow-2xl transition-all duration-500 border-l-4 border-secondary-500"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
              >
                <div className="mb-6">
                  <div className="inline-block px-4 py-1 bg-secondary-50 rounded-full text-xs uppercase tracking-wider font-semibold text-secondary-700 mb-4">
                    {t.about.vision}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 group-hover:text-secondary-600 transition-colors duration-300">
                    {t.about.visionTitle}
                  </h3>
                </div>
                <p className="text-sm md:text-base text-secondary-600 leading-relaxed">
                  {t.about.visionDescription}
                </p>

                {/* Decorative element */}
                <div className="absolute top-8 right-8 w-20 h-20 bg-secondary-100 rounded-full opacity-0 group-hover:opacity-20 blur-2xl transition-opacity duration-500"></div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Values - Modern Minimalist Design */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
                {t.about.valuesTitle}
              </h2>
              <p className="text-base md:text-lg text-secondary-600 max-w-2xl mx-auto">
                {t.about.valuesSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  className="group relative bg-white rounded-2xl p-6 md:p-8 hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: index * 0.15 }}
                  viewport={{ once: true }}
                >
                  {/* Gradient accent on hover */}
                  <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${value.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
                  
                  {/* Number indicator */}
                  <div className="flex items-start justify-between mb-4">
                    <div className={`text-6xl md:text-7xl font-bold bg-gradient-to-r ${value.color} bg-clip-text text-transparent opacity-20 group-hover:opacity-30 transition-opacity duration-500`}>
                      {value.number}
                    </div>
                  </div>
                  
                  <div className="relative z-10">
                    <h4 className="text-xl md:text-2xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">
                      {value.title}
                    </h4>
                    <p className="text-sm md:text-base text-secondary-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>

                  {/* Subtle background decoration */}
                  <div className={`absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl ${value.color} opacity-0 group-hover:opacity-5 blur-2xl transition-opacity duration-500`}></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>



    </div>
  )
}

export default AboutScreen 
 
 
